.wrap {
  position: relative;
  display: inline-block;
}

.trigger {
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  max-width: 360px;
  padding: 0 var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-primary);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  box-sizing: border-box;
  transition:
    border-color var(--duration-fast) var(--ease-default),
    box-shadow var(--duration-fast) var(--ease-default);
}

.trigger:hover:not(:disabled) {
  border-color: var(--border-strong);
}

.trigger:focus-visible,
.triggerOpen {
  border-color: var(--active);
  box-shadow: 0 0 0 3px var(--active-muted);
  outline: none;
}

.trigger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.triggerLabel {
  color: var(--text-muted);
  flex-shrink: 0;
}

.triggerValue {
  font-weight: var(--weight-semi);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chevron {
  color: var(--text-muted);
  flex-shrink: 0;
  transition: transform var(--duration-fast) var(--ease-default);
}

.chevronOpen {
  transform: rotate(180deg);
  color: var(--active);
}

/* ── clear (✕) — revealed on hover/focus when a portfolio is set ────────────
   A sibling button overlaid where the chevron sits (never nested inside the
   trigger <button>). Mounted whenever there is a selection to clear; shown only
   when the control is hovered or holds keyboard focus. Danger-coloured so it
   reads as a destructive action. */
.clear {
  all: unset;
  position: absolute;
  top: 50%;
  right: var(--space-3);
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: var(--danger, #e5484d);
  cursor: pointer;
  border-radius: var(--radius-sm);
  z-index: 1;
  opacity: 0;
  pointer-events: none;
}

/* Reveal the ✕ (and hide the chevron) on hover or keyboard focus within the
   control — only when there is a selection to clear (`.clearable`). */
.clearable:hover .clear,
.clearable:focus-within .clear {
  opacity: 1;
  pointer-events: auto;
}

.clearable:hover .chevron,
.clearable:focus-within .chevron {
  visibility: hidden;
}

.clear:hover {
  color: var(--danger-strong, var(--danger, #e5484d));
}

.clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--active-muted);
}

.menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  width: 320px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.search {
  position: relative;
  padding: var(--space-2);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.searchIcon {
  position: absolute;
  left: calc(var(--space-2) + 8px);
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.searchInput {
  all: unset;
  display: block;
  width: 100%;
  height: 30px;
  padding: 0 var(--space-2) 0 28px;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-primary);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-sm);
  box-sizing: border-box;
}

.searchInput::placeholder {
  color: var(--input-placeholder);
}

.searchInput:focus {
  border-color: var(--input-border-focus);
}

.searchInput:focus-visible {
  outline: none;
}

.list {
  overflow-y: auto;
  flex: 1;
  padding: var(--space-1) 0;
}

.group + .group {
  border-top: 1px solid var(--border-subtle);
  margin-top: var(--space-1);
  padding-top: var(--space-1);
}

.groupLabel {
  padding: var(--space-2) var(--space-3) var(--space-1);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.option {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  cursor: pointer;
}

.option::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--accent-secondary);
  opacity: 0;
}

.optionActive {
  background: linear-gradient(90deg, rgba(240, 180, 41, 0.1) 0%, transparent 70%);
  color: var(--text-primary);
}

.optionActive::before {
  opacity: 1;
}

.optionSelected {
  color: var(--text-primary);
  font-weight: var(--weight-semi);
}

.optionName {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.check {
  color: var(--accent-secondary);
  flex-shrink: 0;
}

.empty {
  padding: var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-muted);
}
